/**
 * The counter's rules, moved out of the UI library verbatim.
 *
 * In `@engyalo/commerce-ui-components` this is `Counter.utils.ts`: 12 exported
 * functions and 56 tests that already hold every quantity rule — steps, caps,
 * stock, pallet/layer rounding. `Counter.tsx` then wraps them in 319 LOC of
 * `useState`/`useMemo`/`useEffect`. The rules were never the React part, so
 * they live here and `counterMachine.ts` supplies the state transition.
 *
 * Two changes from the source, neither touching logic:
 *   - `isIconButtonProps` is gone. It is a type guard over MUI's
 *     `IconButtonProps`, so it is component surface, not a rule.
 *   - the types come from `./counterRules.types`, which vendors the prop shapes
 *     without `Counter.types.ts`' `@mui/material` import.
 *
 * Everything from `roundNumberToNextDecimalStep` down is byte-identical to the
 * source, comments included. A `counterRules.parity.test.ts` would be the way to
 * keep it that way if the library ever becomes a workspace package.
 */
import { EValidate } from './counterRules.types'
import type {
  IActions,
  ICalculateMaxValueProps,
  ICalculateMinValueProps,
  IConditionsForIsDisabled,
  IGetLimitsCondition,
  IGetMaxValueProps,
  IGetMaxValueResult,
  IIsDisabledProps,
  IIsDisabledPropsRequired,
  INextValueProps,
  IResolveTypedInputValueProps,
  ResolveTypedInputOutcome,
  IRoundingFlooringRules,
  IRoundOrFloorValueProps,
} from './counterRules.types'

export * from './counterRules.types'

export interface IExecuteCounterActionsProps {
  value: number
  newValue: number
  resultFixed?: boolean
  step: number
  data?: unknown
  disabled: boolean
  actions?: IActions
  disabledActions?: IActions
  setValues: (value: number) => void
}

/**
 * Round number to next decimal step
 * Consider that 0.5 is the middle of the step, so it will round to the upper step.
 * For other cases, it will round to the nearest step.
 * @param number number to round
 * @returns number, rounded number
 */
export const roundNumberToNextDecimalStep = (number: number): number => {
  const numberToString = number.toString()
  let decimal = number - parseInt(numberToString, 10)
  decimal = Math.round(decimal * 10)
  if (decimal === 5 || decimal === 0) return number
  if (decimal <= 3 || decimal >= 7) {
    return Math.round(number)
  }
  return parseInt(numberToString, 10) + 0.5
}

/**
 * Execute actions if they are defined and not disabled.
 * If disabled is true, it will execute disabledActions if defined.
 */
export const executeCounterActions = ({
  disabled,
  setValues,
  actions,
  disabledActions,
  resultFixed = false,
  newValue,
  ...rest
}: IExecuteCounterActionsProps) => {
  if (disabled) {
    if (disabledActions) {
      disabledActions({
        resultFixed,
        newValue,
        ...rest
      })
    }
  } else {
    setValues(newValue)
    if (actions) {
      actions({
        ...rest,
        resultFixed,
        newValue
      })
    }
  }
}

const getLowerLimit = (possibleValues: unknown[]): number =>
  Math.min(...possibleValues.filter((value) => typeof value === 'number'))

const getUpperLimit = (possibleValues: unknown[]): number =>
  Math.max(...possibleValues.filter((value) => typeof value === 'number'))

const conditionsIsDisabledArray: IConditionsForIsDisabled[] = [
  {
    condition: ({ validate }: IIsDisabledPropsRequired) => validate === EValidate.MIN,
    transformer: ({ value, step, min }) => value - step < min
  },
  {
    condition: ({ validate }: IIsDisabledPropsRequired) => validate === EValidate.MAX,
    transformer: ({ value, step, max, stock }) => {
      const lowerLimitForMaxDisabled = getLowerLimit([max, stock])
      return lowerLimitForMaxDisabled < value + step
    },
  },
  {
    condition: ({ validate }: IIsDisabledPropsRequired) => validate === EValidate.BOTH,
    transformer: ({ value, step, min, max, stock }) => {
      const minDisabled = value - step < min
      const lowerLimitForMaxDisabled = getLowerLimit([max, stock])
      const maxDisabled = lowerLimitForMaxDisabled < value + step
      return minDisabled || maxDisabled
    }
  }
]

/**
 * Check if counter has to be disabled.
 * If validate is 'min', it will check if value is less than min.
 * If validate is 'max', it will check if value is greater than max or stock.
 * If validate is not defined, it will check both.
 */
export const isDisabled = (args: IIsDisabledProps): boolean => {
  const { step = 1, min = 0, validate = 'both' } = args
  const requiredArgs: IIsDisabledPropsRequired = {
    ...args,
    step,
    min,
    validate
  }
  return conditionsIsDisabledArray.find(({ condition }) => condition(requiredArgs))?.transformer?.(requiredArgs) ?? false
}

/**
 * Resolve a typed-input value through three discrete branches:
 *   1. `allowSetMaxvalue` bypass — let `getMaxValue` handle cap clamping downstream.
 *   2. Legacy path (`clampTypedInputToCap` off) — any disable failure reverts to the current value.
 *      Preserves the historical behavior for all existing callers.
 *   3. Opt-in path (`clampTypedInputToCap` on) — over-cap typed values are clamped to the cap
 *      (the bug fix: typing `value === cap` is allowed; typing `value > cap` clamps to cap
 *      instead of reverting to the current value). Below-min violations still revert,
 *      preserving the legacy min behavior.
 *
 * Pure function; extracted so `handleInput` reads as a linear pipeline and so each
 * branch is unit-testable in isolation.
 */
export const resolveTypedInputValue = ({
  modifiedValue,
  currentValue,
  min,
  max,
  allowSetMaxvalue,
  clampTypedInputToCap
}: IResolveTypedInputValueProps): ResolveTypedInputOutcome => {
  if (allowSetMaxvalue) {
    return { kind: 'accepted', value: modifiedValue }
  }

  if (!clampTypedInputToCap) {
    const outOfRange = isDisabled({ value: modifiedValue, min, max })
    return outOfRange
      ? { kind: 'reverted', value: currentValue }
      : { kind: 'accepted', value: modifiedValue }
  }

  // Opt-in path: `clampTypedInputToCap = true`. The typed value falls into one of
  // three sub-cases. `max` here is the effective cap as computed by
  // `calculateMaxValue` (see that function for the exact precedence).

  // (1) Over the cap → clamp to the cap: typing `value === cap` is allowed,
  //     and typing `value > cap` lands on the cap rather than reverting all
  //     the way back to `currentValue`.
  if (typeof max === 'number' && modifiedValue > max) {
    return { kind: 'clampedToCap', value: max, cap: max }
  }

  // (2) Below the min → revert to `currentValue`. Preserves legacy min behavior;
  //     `min` is the only field consulted, so `max`/`stock` are intentionally
  //     omitted from this `isDisabled` call.
  const isBelowMin = isDisabled({
    value: modifiedValue,
    min,
    validate: 'min'
  })
  if (isBelowMin) {
    return { kind: 'reverted', value: currentValue }
  }

  // (3) In range → accept the typed value as-is.
  return { kind: 'accepted', value: modifiedValue }
}

export const getMaxValue = ({
  allowSetMaxvalue,
  initialValue,
  newValue,
  stock,
  max,
  oldValue
}: IGetMaxValueProps): IGetMaxValueResult => {
  let resultValue = newValue
  let resultFixed = false
  const maxValue = Math.max(stock || 0, max || 0)
  if (
    allowSetMaxvalue &&
    oldValue === initialValue &&
    initialValue !== newValue &&
    newValue > maxValue &&
    maxValue > 0
  ) {
    resultValue = maxValue
    resultFixed = true
  }
  return { resultValue, resultFixed }
}

export interface ICalculateMaxValueConditionProps {
  maxFromList?: number
  maxValue?: number
}

export const calculateMaxValue = ({ maxValue, missingProductsReachMax, currentQuantity, stock }: ICalculateMaxValueProps): number | undefined => {
  const maxFromList = missingProductsReachMax !== undefined ? currentQuantity + missingProductsReachMax : undefined
  const possibleValues = [maxFromList, maxValue, stock]
  const lowerLimit = getLowerLimit(possibleValues)
  return lowerLimit === Infinity ? undefined : lowerLimit
}

export const calculateMinValue = ({ min, productMinQty }: ICalculateMinValueProps): number => getUpperLimit([min, productMinQty])

const getLimitsConditions: IGetLimitsCondition[] = [
  {
    condition: ({ roundRuleLimit, newValue }) => roundRuleLimit < newValue,
    transformer: ({ roundRuleLimit, thresholdDown }) => ({ minLimit: roundRuleLimit, maxLimit: roundRuleLimit + thresholdDown })
  },
  {
    condition: ({ roundRuleLimit, newValue }) => roundRuleLimit > newValue,
    transformer: ({ roundRuleLimit, thresholdUp }) => ({ minLimit: roundRuleLimit - thresholdUp, maxLimit: roundRuleLimit })
  },
]

export const getNextValidValue = ({ prevValue: maybePrevValue, rules = [], step, max = Infinity, min = 0, }: INextValueProps): number => {
  // If the previous value is less than the minimum allowed, set prevValue to one step below the minimum, otherwise use maybePrevValue.
  const prevValue = maybePrevValue < min ? (Math.ceil(min / step) - 1) * step : maybePrevValue
  let newValue = prevValue
  let maybeNewValue = prevValue
  let isValidRounded = false
  let isValidMax = true
  do {
    const remainder = maybeNewValue % step
    maybeNewValue = maybeNewValue + (step - remainder)
    maybeNewValue = roundOrFloorValue({ prevValue, newValue: maybeNewValue, rules })
    isValidRounded = maybeNewValue % step === 0
    isValidMax = maybeNewValue <= max
    if (isValidRounded && isValidMax) {
      newValue = maybeNewValue
    }
  } while (!isValidRounded && isValidMax)
  return newValue
}

export const getPrevValidValue = ({ prevValue, rules = [], step, min = 0 }: INextValueProps): number => {
  // If decrementing from prevValue by step would go below the minimum limit (and min > 0),
  // return 0 instead. This allows the counter to jump directly to zero from the first step below min.
  if (prevValue - step < min && min > 0) {
    return 0
  }
  let newValue = prevValue
  let maybeNewValue = prevValue
  let isValidRounded = false
  let isValidMin = true
  do {
    const remainder = maybeNewValue % step

    maybeNewValue = remainder ? maybeNewValue - remainder : maybeNewValue - step

    maybeNewValue = roundOrFloorValue({ prevValue, newValue: maybeNewValue, rules })

    isValidRounded = maybeNewValue % step === 0

    isValidMin = maybeNewValue >= min

    if (isValidRounded && isValidMin) {
      newValue = maybeNewValue
    }
  } while (!isValidRounded && isValidMin)

  return newValue
}

export const roundOrFloorValue = ({ prevValue, newValue, rules = [] }: IRoundOrFloorValueProps): number => {
  const diff = newValue - prevValue
  const isAdding = diff > 0

  const rulesToIterate = isAdding ? rules : [...rules].reverse()

  const calculatedValue = rulesToIterate.reduce<number>((acc, { value: roundRuleValue, thresholdUp, thresholdDown }) => {
    if (roundRuleValue === 0) return acc
    const roundRuleLimit = Math.round(acc / roundRuleValue) * roundRuleValue
    if (roundRuleLimit === 0) return acc
    const getLimitsParams = { roundRuleLimit, newValue: acc, thresholdUp, thresholdDown }
    const limits = getLimitsConditions.find(({ condition }) => condition(getLimitsParams))?.transformer?.(getLimitsParams)
    if (!limits) return acc
    const isInRange = acc >= limits.minLimit && acc <= limits.maxLimit
    if (isAdding) {
      // When increasing the value and the computed maxLimit lies exactly on the (roundRuleLimit + thresholdDown)
      // boundary, we extend the upper bound by +1 for positive thresholds. This avoids treating the boundary
      // value itself as part of the "invalid" rounding window and prevents off‑by‑one issues where a value that
      // should still be rounded up is instead clamped too early at the threshold boundary.
      const maxLimit = limits.maxLimit === roundRuleLimit + thresholdDown && thresholdDown > 0 ? limits.maxLimit + 1 : limits.maxLimit
      return isInRange ? maxLimit : acc
    } else {
      // When decreasing the value and the computed minLimit lies exactly on the (roundRuleLimit - thresholdUp)
      // boundary, we shrink the lower bound by -1 for positive thresholds. This ensures the boundary value is
      // not incorrectly treated as inside the "invalid" window, avoiding off‑by‑one errors where a value that
      // should still be rounded down is blocked at the threshold boundary.
      const minLimit = limits.minLimit === roundRuleLimit - thresholdUp && thresholdUp > 0 ? limits.minLimit - 1 : limits.minLimit
      return isInRange ? minLimit : acc
    }
  }, newValue)

  return calculatedValue
}

export const getRuleInvalidatedBy = (value: number, rules: IRoundingFlooringRules[] = []): IRoundingFlooringRules | undefined => {
  const reversedRules = [...rules].reverse()

  const ruleInvalidatedBy = reversedRules.find(({ value: roundRuleValue, thresholdUp, thresholdDown }) => {
    const roundRuleLimit = Math.round(value / roundRuleValue) * roundRuleValue
    if (roundRuleLimit === 0) return false
    const getLimitsParams = { roundRuleLimit, newValue: value, thresholdUp, thresholdDown }
    const limits = getLimitsConditions.find(({ condition }) => condition(getLimitsParams))?.transformer?.(getLimitsParams)
    if (!limits) return false
    const isInRange = value >= limits.minLimit && value <= limits.maxLimit
    return isInRange
  })

  return ruleInvalidatedBy
}